// 引入语言文件
import enLocale from '@user-center/locales/chinaKeys/en.json'
import jaJPLocale from '@user-center/locales/chinaKeys/ja_JP.json'
import viVNLocale from '@user-center/locales/chinaKeys/vi_VN.json'
import zhHansLocale from '@user-center/locales/chinaKeys/zh-Hans.json'
import zhHantLocale from '@user-center/locales/chinaKeys/zh-TW.json'

import enEnKeyLocale from '@user-center/locales/enKeys/en.json'
import jaJpEnKeyLocale from '@user-center/locales/enKeys/ja_JP.json'
import viVNEnKeyLocale from '@user-center/locales/enKeys/vi_VN.json'
import zhHansEnKeyLocale from '@user-center/locales/enKeys/zh-CN.json'
import zhTwEnKeyLocale from '@user-center/locales/enKeys/zh-TW.json'

import { cloneDeep, get, merge } from 'lodash-es'
import { createI18n } from 'vue-i18n'

type LocaleMessageMap = Record<string, any>

interface LocaleSourceModules {
  enLocale: any
  jaJPLocale: any
  viVNLocale: any
  zhHansLocale: any
  zhHantLocale: any
  zhHansEnKeyLocale: any
  enEnKeyLocale: any
  viVNEnKeyLocale: any
  zhTwEnKeyLocale: any
  jaJpEnKeyLocale: any
}

const localeSourceModules: LocaleSourceModules = {
  enLocale,
  jaJPLocale,
  viVNLocale,
  zhHansLocale,
  zhHantLocale,
  zhHansEnKeyLocale,
  enEnKeyLocale,
  viVNEnKeyLocale,
  zhTwEnKeyLocale,
  jaJpEnKeyLocale,
}

function buildMessages(sourceModules: LocaleSourceModules): LocaleMessageMap {
  const newHantJson: any = merge(
    cloneDeep(sourceModules.zhHansLocale),
    cloneDeep(sourceModules.zhHantLocale),
  )

  return {
    'zh-CN': merge(cloneDeep(sourceModules.zhHansLocale), cloneDeep(sourceModules.zhHansEnKeyLocale)),
    'zh-Hans': merge(cloneDeep(sourceModules.zhHansLocale), cloneDeep(sourceModules.zhHansEnKeyLocale)),
    'zh-Hant': merge(cloneDeep(newHantJson), cloneDeep(sourceModules.zhTwEnKeyLocale)),
    'zh-TW': merge(cloneDeep(newHantJson), cloneDeep(sourceModules.zhTwEnKeyLocale)),
    'en': merge(cloneDeep(sourceModules.enLocale), cloneDeep(sourceModules.enEnKeyLocale)),
    'ja_JP': merge(cloneDeep(sourceModules.jaJPLocale), cloneDeep(sourceModules.jaJpEnKeyLocale)),
    'ja': merge(cloneDeep(sourceModules.jaJPLocale), cloneDeep(sourceModules.jaJpEnKeyLocale)),
    'vi_VN': merge(cloneDeep(sourceModules.viVNLocale), cloneDeep(sourceModules.viVNEnKeyLocale)),
    'vi': merge(cloneDeep(sourceModules.viVNLocale), cloneDeep(sourceModules.viVNEnKeyLocale)),
  }
}

// function tranStrToAllKeys(obj: any): void {
//   lodash.forEach(obj, function (value: any, key: any) {
//     if (lodash.isObject(value)) {
//       tranStrToAllKeys(value);
//     } else {
//       obj[key] = zhTranFn(obj[key], "t");
//     }
//   });
// }
// tranStrToAllKeys(newHantJson);

let messages: LocaleMessageMap = buildMessages(localeSourceModules)

const DEFAULT_LOCALE = 'zh-Hant'

function getBrowserStorage(): Storage | null {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage
  }
  if (typeof localStorage !== 'undefined') {
    return localStorage
  }
  return null
}

function getStoredLocale(key: string): string | null {
  return getBrowserStorage()?.getItem(key) || null
}

function setStoredLocale(key: string, value: string): void {
  getBrowserStorage()?.setItem(key, value)
}

function resolveInitialLocale(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_LOCALE
  }

  const currentUserCenterLocale = getStoredLocale('current_user_center_locale')
  if (currentUserCenterLocale) {
    setStoredLocale('now-locale', currentUserCenterLocale)
    return currentUserCenterLocale
  }

  const currentLocale = getStoredLocale('now-locale')
  if (currentLocale) {
    return currentLocale
  }

  setStoredLocale('now-locale', DEFAULT_LOCALE)
  return DEFAULT_LOCALE
}

let nowLocale: any = resolveInitialLocale()
// 创建 i18n 实例
const i18n = createI18n({
  locale: nowLocale, // 设置默认语言
  fallbackLocale: DEFAULT_LOCALE, // 如果当前语言没有对应的翻译，则回退到默认语言
  messages, // 设置语言信息
})

function replaceLocaleMessages(nextMessages: LocaleMessageMap): void {
  messages = nextMessages
  Object.keys(nextMessages).forEach((locale) => {
    i18n.global.setLocaleMessage(locale, nextMessages[locale])
  })
}

if (import.meta.hot) {
  import.meta.hot.accept([
    '@user-center/locales/chinaKeys/en.json',
    '@user-center/locales/chinaKeys/ja_JP.json',
    '@user-center/locales/chinaKeys/vi_VN.json',
    '@user-center/locales/chinaKeys/zh-Hans.json',
    '@user-center/locales/chinaKeys/zh-TW.json',
    '@user-center/locales/enKeys/zh-CN.json',
    '@user-center/locales/enKeys/en.json',
    '@user-center/locales/enKeys/vi_VN.json',
    '@user-center/locales/enKeys/zh-TW.json',
    '@user-center/locales/enKeys/ja_JP.json',
  ], (modules) => {
    if (!modules || modules.length < 10) {
      return
    }

    const [
      nextEnLocale,
      nextJaJPLocale,
      nextViVNLocale,
      nextZhHansLocale,
      nextZhHantLocale,
      nextZhHansEnKeyLocale,
      nextEnEnKeyLocale,
      nextViVNEnKeyLocale,
      nextZhTwEnKeyLocale,
      nextJaJpEnKeyLocale,
    ] = modules.map((item: any) => item?.default ?? item)

    replaceLocaleMessages(buildMessages({
      enLocale: nextEnLocale,
      jaJPLocale: nextJaJPLocale,
      viVNLocale: nextViVNLocale,
      zhHansLocale: nextZhHansLocale,
      zhHantLocale: nextZhHantLocale,
      zhHansEnKeyLocale: nextZhHansEnKeyLocale,
      enEnKeyLocale: nextEnEnKeyLocale,
      viVNEnKeyLocale: nextViVNEnKeyLocale,
      zhTwEnKeyLocale: nextZhTwEnKeyLocale,
      jaJpEnKeyLocale: nextJaJpEnKeyLocale,
    }))
  })
}

export function $t(name: string, data?: any): string {
  const currentLocale: any = get(i18n, 'global.locale.value') || getStoredLocale('now-locale') || DEFAULT_LOCALE
  if (get(i18n, 'global.t')) {
    return i18n.global.t(name, data)
  }
  let setText: string
    = (messages[currentLocale] && get(messages[currentLocale], name))
      || name
  if (setText && data && Object.keys(data) && Object.keys(data).length > 0) {
    Object.keys(data).forEach((k) => {
      if (typeof data[k] != 'undefined') {
        setText = setText.replace(`{${k}}`, data[k])
      }
    })
  }
  return setText
}

export function isLongLocale(locale: string): boolean {
  const LongLocaleMap = {
    en: true,
    ja_JP: true,
    ja: true,
    vi_VN: true,
    vi: true,
  }
  return !!LongLocaleMap[locale]
}

export function isChinaLocale(locale: string): boolean {
  const chinaLocaleMap = {
    'zh-CN': true,
    'zh-Hans': true,
    'zh-Hant': true,
    'zh-TW': true,
  }
  return !!chinaLocaleMap[locale]
}
export function isTwLocale(locale?: string): boolean {
  const chinaLocaleMap = {

    'zh-Hant': true,
    'zh-TW': true,
  }

  return !!chinaLocaleMap[locale || getCurrentLocale()]
}
export function getCurrentLocale() {
  return get(i18n, 'global.locale.value') || getStoredLocale('now-locale') || DEFAULT_LOCALE
}
export function getCurrentLocaleTransferCode() {
  const code = getCurrentLocale()
  const codeMap = {
    vi_VN: 'vi',
    ja_JP: 'ja',
  }
  return codeMap[code] ? codeMap[code] : code
}
export function isToTransfer(locale?: string): boolean {
  if (!locale) {
    locale = get(i18n, 'global.locale.value') || getStoredLocale('now-locale') || DEFAULT_LOCALE
  }

  return !isChinaLocale(locale)
}

export function toggleLocale(locale: string): boolean {
  setStoredLocale('current_user_center_locale', locale)
  setStoredLocale('now-locale', locale)
  nowLocale = locale
  if (typeof window !== 'undefined') {
    window.location.reload()
  }
  return true
}

export function getLocaleText(locale?: string): string {
  if (!locale) {
    locale = getStoredLocale('now-locale') || DEFAULT_LOCALE
  }
  const shotTexts = {
    'zh-Hans': 'zh',
    'zh-Hant': 'zh-tw',
    'zh-TW': 'zh-tw',
    'en': 'en',
    'ja_JP': 'ja',
    'ja': 'ja',
    'vi': 'vi',
    'vi_VN': 'vi',
  }
  return shotTexts[locale] || ''
}

export default i18n
